import invariant from 'tiny-invariant';

/**
 * This function de-duplicates data by their `attribute`, ensuring that no two data points in the
 * same array have the same `attribute` value.
 *
 * Example:
 *
 * ```ts
 * const data = [
 *   { id: 1, value: 1 },
 *   { id: 2, value: 2 },
 *   { id: 1, value: 3 },
 *   { id: 3, value: 4 },
 * ];
 *
 * const result = dedupBy('id', data);
 *
 * // result = [
 * //   [{ id: 1, value: 1 }, { id: 2, value: 2 }, { id: 3, value: 4 }],
 * //   [{ id: 1, value: 3 }],
 * // ];
 * ```
 */
export function splitDuplicatesBy<T>(attribute: keyof T, data: T[]): T[][] {
  const result: T[][] = [];

  for (const point of data) {
    let arrayIndex = -1;
    // Find the first array without the attribute
    for (let i = 0; i < result.length; i++) {
      if (result[i].findIndex((p) => p[attribute] === point[attribute]) === -1) {
        arrayIndex = i;
        break;
      }
    }

    if (arrayIndex !== -1) {
      // Add the point to the array
      result[arrayIndex].push(point);
    } else {
      // No array without the attribute found, create a new one
      result.push([point]);
    }
  }

  return result;
}

export function haveDifferentElements<T>(a: T[], b: T[]): boolean {
  return a.length !== b.length || a.some((value) => !b.includes(value));
}

export function zipArrays<T>(a: T[], b: T[]): [T, T][] {
  invariant(a.length === b.length, 'Expected arrays to have the same length');

  return a.map((value, index) => [value, b[index]]);
}
